import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useAuth } from '@modules/me';
import { useBranchesWithDeliveryPoints } from '@modules/branches';
import { useBranchPartners } from '@modules/branch-partners';
import { useCashRegisters } from '@modules/cash-flow';
import { UsersService } from '../../services';

const REFERENCE_QUERY_OPTIONS = { staleTime: 5 * 60 * 1000 };

export const useUserPermissions = (userId: string) => {
  const { state } = useAuth();
  const isSelf = state.user.data?.id?.toString() === userId;

  const [operationIds, setOperationIds] = useState<number[]>([]);
  const [cashboxId, setCashboxId] = useState<number | undefined>(undefined);
  const [adminBranchId, setAdminBranchId] = useState<number | undefined>(undefined);
  const [companyId, setCompanyId] = useState<number | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);

  // Deployment configuration, not per-user data — cached instead of refetched every time the
  // permissions modal opens.
  const { data: operations, isLoading: operationsLoading } = useQuery(
    ['operations'],
    async () => {
      const result = await UsersService.getOperations();
      return result.status === 200 ? result.data : [];
    },
    REFERENCE_QUERY_OPTIONS,
  );

  const { data: cashboxes } = useCashRegisters();
  const { data: companies } = useBranchPartners();
  const { data: allBranches } = useBranchesWithDeliveryPoints();

  // Only the selected company's branches can be assigned, as in the old project.
  const branches = useMemo(() => (allBranches ?? []).filter((branch) => branch.companyId === companyId), [allBranches, companyId]);

  const { isLoading: permissionsLoading } = useQuery(
    ['user-permissions', userId],
    async () => {
      const result = await UsersService.getUserPermissions(userId);
      return result.status === 200 ? result.data : null;
    },
    {
      enabled: !!userId,
      onSuccess: (data) => {
        if (data) {
          setOperationIds(data.permissionIds);
          if (data.companyId) setCompanyId(data.companyId);
        }
      },
    },
  );

  useQuery(
    ['user-permissions-detail', userId],
    async () => {
      const result = await UsersService.getUserById(userId);
      return result.status === 200 ? result.data : null;
    },
    {
      enabled: !!userId,
      onSuccess: (data) => {
        if (data) {
          if (data.cashboxId) setCashboxId(data.cashboxId);
          if (data.adminBranchId) setAdminBranchId(data.adminBranchId);
        }
      },
    },
  );

  const toggleOperation = (id: number) => {
    const allOps = (operations ?? []).flatMap((g) => g.operations);
    const changePermsOp = allOps.find((op) => op.codeName === 'changeuserpermissions');
    if (changePermsOp?.id === id && !operationIds.includes(id)) {
      setOperationIds(allOps.map((op) => op.id));
    } else {
      setOperationIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    }
  };

  const submit = async () => {
    setSubmitting(true);
    const result = await UsersService.updateUserPermissions(userId, operationIds, cashboxId, adminBranchId);
    setSubmitting(false);
    return result;
  };

  return {
    operations: operations ?? [],
    cashboxes: cashboxes ?? [],
    companies: companies ?? [],
    branches,
    operationIds,
    cashboxId,
    adminBranchId,
    companyId,
    isSelf,
    setCashboxId,
    setAdminBranchId,
    setCompanyId,
    toggleOperation,
    submit,
    isLoading: operationsLoading || permissionsLoading,
    submitting,
  };
};
